import { useCallback, useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import classNames from "classnames";
import { useNavigate } from "react-router-dom";
import weekOfYear from "dayjs/plugin/weekOfYear";
import {
  NavLeftArrow,
  NavRightArrow,
  PencilIcon,
  RedIndicator,
  BlueIndicator,
  GreenIndicator,
  Spinner,
} from "components/atoms/Icon";
import { Typography, Button } from "components/atoms";
import {
  FetchError,
  MonthNavigator,
  RefreshModal,
  StudyPlannerScheduleCard,
  StudyPlannerScheduleDetailedCard,
  LoadingOverlay,
  BackButton,
} from "components/molecules";
import { useAppDispatch, useAppSelector, useScreenSize } from "hooks";
import { StudyModal } from "./StudyModal";
import { SideFilterPanel } from "./SideFilterPanel";
import { DropContainer } from "./DropContainer";
import {
  studyPlanner,
  useGetStudyPlannerEventQuery,
  useGetStudyPlannerEventsQuery,
  useGetStudyPlannerFormQuery,
  useUpdateEventMutation,
  useValidateEventNewDateMutation,
} from "services/studyPlanner";
import { ValidateModal } from "./ValidateModal";
import { formatDateDuration } from "utils/functions/formatDateDuration";
import { formatToLocalDate } from "utils/functions/formatToLocalDate";
import { getTimeZone } from "utils/functions/getTimeZone";
import { LaunchSessionButton } from "./LaunchSessionButton";
import "./index.scss";
import { mixpanelHelper } from "utils/functions/mixPanelHelper";
import { decodeToken } from "utils/functions/decodeToken";
import {
  useGetCourseworkQuery,
  useGetOverallProgressQuery,
  useGetTestScoresAndPerformanceQuery,
} from "services/homeApi";
import { useSegmentReFetch } from "context";
import { getObjectKeysByValue } from "services/helpers";
import { getTimeFilterInput } from "utils/functions/getTimeFilterInput";
import { useLazyGetCourseIndexQuery } from "services/courseIndex";
import {
  replaceArrayOfObjects,
  reStructureSegment,
} from "utils/functions/courseWorkReStructure";
import { ai_mcat_chat_status } from "state/reducers/layoutSlice";
import { HorizontalScrollHandler } from "./HorizontalScrollHandler";

dayjs.extend(utc);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

interface IProps {
  theme: "light" | "dark";
}

const daysofTheWeek = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

export const StudyPlanner = ({ theme }: IProps) => {
  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const calendarSectionRef = useRef<HTMLDivElement>(null);

  const { aiMCATChatDrawerStatus } = useAppSelector(
    (state: { layout: { aiMCATChatDrawerStatus: ai_mcat_chat_status } }) =>
      state.layout
  );

  const dispatch = useAppDispatch();

  const [modalState, setModalState] = useState({
    showModal: false,
    type: "Prework",
  });

  const [isDelete, setIsDelete] = useState(false);
  const [minMonthNo, setMinMonthNo] = useState<any>();
  const [maxMonthNo, setMaxMonthNo] = useState<any>();
  const [isMonthPopUpShow, setIsMonthPopUpShow] = useState(false);
  const [isYearPopUpShow, setIsYearPopUpShow] = useState(false);

  const [sideModalState, setSideModalState] = useState<{
    isOpen: boolean;
    displayDate: string;
  }>({
    isOpen: false,
    displayDate: "",
  });

  const [validateModal, setValidateModal] = useState<any>({
    isShowModal: false,
    payload: {
      Status: "",
      Message: "",
      eventInfo: "",
      newDate: "",
    },
  });

  const payload = decodeToken();

  const userId = payload ? payload.userid : null;
  const productCode = payload ? payload.productcode : null;
  const offeringCode = payload ? payload.offeringcode : null;

  const {
    refetch: refetchAllEvents,
    isLoading,
    isFetching,
    isError,
    data,
  } = useGetStudyPlannerEventsQuery();
  dayjs.extend(weekOfYear);

  const { data: formData } = useGetStudyPlannerFormQuery();
  const [
    validateEvent,
    { isLoading: isValidateLoading, isError: isValidateError },
  ] = useValidateEventNewDateMutation();

  const [
    updateEvent,
    { isLoading: updateEventIsLoading, isError: updateEventIsError },
  ] = useUpdateEventMutation();

  const [eventList, setEventList] = useState<any>([]);
  const [mobileDay, setMobileDay] = useState<any>();

  const [refreshModalOpenState, setRefreshModalOpenState] = useState({
    isOpen: false,
    contentObjectType: "",
    eventId: 0,
  });

  const { refetch: refetchGetStudyPlannerEvent, isFetching: isFetchingEvent } =
    useGetStudyPlannerEventQuery(
      {
        eventId: refreshModalOpenState.eventId,
      },
      {
        skip: refreshModalOpenState.eventId === 0,
      }
    );

  const { refetch: overAllProgressRefetch } = useGetOverallProgressQuery({
    Userid: userId,
    Productcode: productCode,
    Offeringcode: offeringCode,
  });

  const {
    data: courseWorkData,
    setData,
    selectedBtn,
    selectedSession,
  } = useSegmentReFetch();

  const [courseworkPage, setCourseWorkPage] = useState(1);

  const [triggerCourseIndexRefetch] = useLazyGetCourseIndexQuery();

  const { refetch: testScoresRefetch } = useGetTestScoresAndPerformanceQuery();

  const { refetch: courseWorkRefetch } = useGetCourseworkQuery({
    page: courseworkPage,
    selectedOption: parseInt(getObjectKeysByValue(selectedBtn, true)[0], 10),
    timeFilter:
      selectedSession === null
        ? 1
        : getTimeFilterInput(Object.values(selectedSession)[0]),
  });

  const handleSetCourseworkPage = (eventData: any) => {
    const selectedSegment =
      courseWorkData &&
      Array.isArray(courseWorkData) &&
      courseWorkData.filter((item) => item.Description === eventData.Title);

    if (
      selectedSegment &&
      Array.isArray(selectedSegment) &&
      selectedSegment[0] &&
      selectedSegment[0].page
    ) {
      setCourseWorkPage((prev) => selectedSegment[0].page);
    }
  };

  const incrementMonth = () => {
    if (
      dayjs(maxMonthNo).diff(dayjs(currentMonth.add(1, "month")), "month") >=
        0 &&
      dayjs(maxMonthNo).format("MMM") !== dayjs(currentMonth).format("MMM")
    ) {
      setCurrentMonth((prevMonth) => prevMonth.add(1, "month"));
      setMobileDay(undefined);
    }
  };

  const popUpIncrementMonth = (month: number) => {
    const currentDate = dayjs()
      .set("month", month)
      .set("year", currentMonth.year());

    const isWithinRange =
      (currentDate.isAfter(dayjs(minMonthNo), "month") &&
        currentDate.isBefore(dayjs(maxMonthNo), "month")) ||
      currentDate.isSame(dayjs(minMonthNo), "month") ||
      currentDate.isSame(dayjs(maxMonthNo), "month");

    if (isWithinRange) {
      setCurrentMonth(currentDate);
      setMobileDay(undefined);
    }

    setIsMonthPopUpShow(false);
    setIsYearPopUpShow(false);
  };

  const popUpIncrementYears = (year: number) => {
    let holdingYear = dayjs(currentMonth).year();

    if (year < holdingYear) {
      if (dayjs().year() === year) {
        if (dayjs().month() >= dayjs(minDate).month()) {
          const currentDate = dayjs()
            .set("month", dayjs().month())
            .set("year", year);

          setCurrentMonth(currentDate);
          setMobileDay(undefined);
        }
      }
    }

    if (year > holdingYear) {
      const currentDate = dayjs().set("month", 0).set("year", year);

      setCurrentMonth(currentDate);
      setMobileDay(undefined);
    }

    setIsMonthPopUpShow((prev) => false);
    setIsYearPopUpShow((prev) => false);
  };

  const decrementMonth = () => {
    if (
      dayjs(minMonthNo).diff(
        dayjs(currentMonth.subtract(1, "month")),
        "month"
      ) <= 0 &&
      dayjs(minMonthNo).format("MMM") !== dayjs(currentMonth).format("MMM")
    ) {
      setCurrentMonth((prevMonth) => prevMonth.subtract(1, "month"));
      setMobileDay(undefined);
    }
  };

  const handleClickToday = () => {
    setCurrentMonth(dayjs());
  };

  const handleModalClose = () => {
    setModalState((prev) => ({ ...prev, showModal: false }));
    setIsDelete((prev) => false);
  };

  const handleValidateModalClose = (isReset: boolean) => {
    if (isReset) {
      dispatch(
        studyPlanner.util.updateQueryData(
          "getStudyPlannerEvents",
          undefined,
          (draft) => {
            const event = draft?.Events?.find(
              (eve: any) =>
                eve.StudyPlannerEventId ===
                validateModal.payload.eventInfo.StudyPlannerEventId
            );
            event.Start =
              validateModal.payload.eventInfo.Start ||
              validateModal.payload.eventInfo.ScheduledDate;

            draft?.Events.forEach((ele: any, index: number) => {
              if (
                ele.StudyPlannerEventId ===
                validateModal.payload.eventInfo.StudyPlannerEventId
              ) {
                draft.Events[index] = event;
              }
            });
          }
        )
      );
    }
    setValidateModal((prev: any) => ({
      ...prev,
      isShowModal: false,
      eventId: "",
      newDate: "",
    }));
  };

  const handleAddItem = (date: string) => {
    setSideModalState({ isOpen: true, displayDate: date });
  };

  const handleRefreshModalClose = () => {
    setRefreshModalOpenState((prev: any) => ({ ...prev, isOpen: false }));
  };

  const handleRefresh = async () => {
    const promises = [
      courseWorkRefetch(),
      refetchAllEvents(),
      overAllProgressRefetch(),
      refetchGetStudyPlannerEvent(),
      testScoresRefetch(),
      triggerCourseIndexRefetch(),
    ];

    Promise.allSettled(promises).then((responses: any) => {
      if (responses[0].status === "fulfilled") {
        const deStructuredArray = reStructureSegment(responses[0].value.data);
        const updatedArray = replaceArrayOfObjects(
          deStructuredArray,
          courseWorkData
        );
        setData([...updatedArray]);
      }
      handleRefreshModalClose();
    });
  };

  const handleMobileDayClick = (clickDay: any) => {
    setMobileDay(clickDay);
  };

  const handleUpdateEvent = async (eventId: any, newDate: any) => {
    try {
      await updateEvent({
        eventId: eventId,
        newDate: newDate,
      })
        .unwrap()
        .then(() => handleValidateModalClose(false));
    } catch (error) {
      console.log(error);
      handleValidateModalClose(true);
    }
  };

  const validateRescheduleDate = async (cardInfo: any, formatDate: any) => {
    if (
      dayjs(formatDate).format("YYYY-MM-DD") >=
        dayjs(minDate).format("YYYY-MM-DD") &&
      dayjs(formatDate).format("YYYY-MM-DD") <
        dayjs(maxDate).format("YYYY-MM-DD")
    ) {
      try {
        await validateEvent({
          eventId: cardInfo.StudyPlannerEventId,
          newDate: formatDate,
        })
          .unwrap()
          .then((payload) => {
            if (payload.Status === "Success") {
              handleUpdateEvent(cardInfo.StudyPlannerEventId, formatDate);
            } else {
              setValidateModal((prev: any) => ({
                ...prev,
                isShowModal: true,
                payload: {
                  Status: payload.Status,
                  Message: payload.Message,
                  eventInfo: cardInfo,
                  newDate: formatDate,
                },
              }));
            }
          });
      } catch (error: any) {
        setValidateModal((prev: any) => ({
          ...prev,
          isShowModal: true,
          payload: {
            Status: error.status,
            Message: error?.data?.Message || "",
            eventInfo: cardInfo,
            newDate: formatDate,
          },
        }));
      }
    }
  };

  const sumOfTimeEstimates = (events: any, currentDay: any): string => {
    const total = events
      .filter(
        (val: any) => dayjs(val.Start).format("DD/MM/YYYY") === currentDay
      )
      .map((obj: any) => obj.TimeEstimate)
      .reduce((acc: any, curr: any) => acc + curr, 0);

    return formatDateDuration(total);
  };

  const maxDate =
    data && data.StudyPlannerForm ? data.StudyPlannerForm.ExamDate : "";
  const minDate = data && data.Events ? data.StudyPlannerForm.StartDate : "";

  useEffect(() => {
    setMinMonthNo(dayjs(minDate)); // Note: minMonthNo state contains minDate from studyplanner form
    setMaxMonthNo(dayjs(maxDate)); // Note: maxMonthNo state contains maxDate from studyplanner form
  }, [maxDate, minDate]);

  const renderBody = (data: any) => {
    const monthStart = currentMonth.startOf("month");
    const monthEnd = currentMonth.endOf("month");
    const startDate = monthStart.startOf("week");
    const endDate = monthEnd.endOf("week");

    const weeks = [];
    let week = [];

    for (let day = startDate; day.isBefore(endDate); day = day.add(1, "day")) {
      if (day.isSameOrAfter(monthStart) && day.isSameOrBefore(monthEnd)) {
        week.push({
          dateNumber: day,
          inCurrentMonth: true,
        });
      } else {
        week.push({
          dateNumber: day,
          inCurrentMonth: false,
        });
      }
      if (day.day() === 6) {
        weeks.push(week);
        week = [];
      }
    }

    return weeks.map((week, i) => (
      <tr key={i}>
        {week.map((day, j) => (
          <td
            key={j}
            className={classNames({
              "bg-disable":
                dayjs(day.dateNumber).format("YYYY-MM-DD") <
                  dayjs(minDate).format("YYYY-MM-DD") ||
                dayjs(day.dateNumber).format("YYYY-MM-DD") >=
                  dayjs(maxDate).format("YYYY-MM-DD"),
            })}
          >
            <div
              className={classNames({
                "tbody-td-parent-wrapper": true,
                "blur-date-number": day.inCurrentMonth === false,
              })}
            >
              <div className="header-date-number-wrapper">
                <Typography
                  variant="h9Book"
                  label={day.dateNumber.format("D")}
                  styles={
                    day.dateNumber.format("DD/MM/YYYY") ===
                    dayjs().format("DD/MM/YYYY")
                      ? "today-date-typography-custom-style day-number-custom-style"
                      : "date-typography-custom-style day-number-custom-style"
                  }
                />
                {day.inCurrentMonth &&
                eventList &&
                eventList.filter(
                  (val: any) =>
                    dayjs(val.Start).format("DD/MM/YYYY") ===
                    day.dateNumber.format("DD/MM/YYYY")
                ).length > 0 ? (
                  <Typography
                    variant="h9Book"
                    label={sumOfTimeEstimates(
                      eventList,
                      day.dateNumber.format("DD/MM/YYYY")
                    )}
                    styles="header-time-custom-style"
                  />
                ) : null}
              </div>
              <DropContainer
                setModalState={setModalState}
                day={day}
                formatDate={day.dateNumber.format("YYYY-MM-DD")}
                cardList={eventList}
                validateRescheduleDate={validateRescheduleDate}
                theme={theme}
              />
              {dayjs(day.dateNumber).isSameOrAfter(
                dayjs(data.StudyPlannerForm.StartDate)
              ) &&
              dayjs(day.dateNumber).isBefore(
                dayjs(data.StudyPlannerForm.ExamDate)
              ) ? (
                <div className="add-item-wrapper">
                  <Button
                    icon={false}
                    type="score"
                    label="Add Event"
                    styles="add-item-button"
                    onClick={() => {
                      handleAddItem(day.dateNumber.format("MMMM D, YYYY"));
                    }}
                  />
                </div>
              ) : null}
            </div>
          </td>
        ))}
      </tr>
    ));
  };

  const mobileRenderBody = () => {
    const monthStart = currentMonth.startOf("month");
    const monthEnd = currentMonth.endOf("month");
    const startDate = monthStart.startOf("week");
    const endDate = monthEnd.endOf("week");

    const weeks = [];
    let week = [];

    for (let day = startDate; day.isBefore(endDate); day = day.add(1, "day")) {
      if (day.isSameOrAfter(monthStart) && day.isSameOrBefore(monthEnd)) {
        week.push({
          dateNumber: day,
          inCurrentMonth: true,
        });
      } else {
        week.push({
          dateNumber: day,
          inCurrentMonth: false,
        });
      }
      if (day.day() === 6) {
        weeks.push(week);
        week = [];
      }
    }

    return weeks.map((week, i) => (
      <tr key={i}>
        {week.map((day, j) => (
          <td key={j}>
            <div
              className={classNames({
                "tbody-td-parent-wrapper": true,
                "blur-date-number": day.inCurrentMonth === false,
              })}
            >
              <div
                className="header-date-number-wrapper"
                onClick={() => handleMobileDayClick(day.dateNumber)}
              >
                <Typography
                  variant="h8Med"
                  label={day.dateNumber.format("D")}
                  styles={
                    day.inCurrentMonth === true &&
                    day.dateNumber.format("DD/MM/YYYY") ===
                      dayjs().format("DD/MM/YYYY")
                      ? "today-date-typography-custom-style day-number-custom-style"
                      : "date-typography-custom-style day-number-custom-style"
                  }
                />
                <div className="dot"></div>
              </div>
            </div>
          </td>
        ))}
      </tr>
    ));
  };

  const screen = useScreenSize();
  const navigate = useNavigate();

  const dataRef = useRef(data);
  useEffect(() => {
    if (data && data.Events) {
      const isRefetch = dataRef.current !== data;
      dataRef.current = data;
      setEventList(data.Events);
      // On refetch (e.g. after iframe close), preserve mobile calendar view:
      // keep the selected date and keep currentMonth showing that month.
      if (isRefetch) {
        setMobileDay((prevDay: any) => {
          if (prevDay && dayjs(prevDay).isValid()) {
            setCurrentMonth(dayjs(prevDay).startOf("month"));
          }
          return prevDay;
        });
      }
    }
  }, [data]);

  return (
    <div className={classNames("study-planner-wrapper")}>
      <BackButton theme={theme} onBack={() => navigate("/")} />
      <div className="title-section">
        <div className="first-part">
          <Typography label="Study Plan" variant="h2Bold" />
        </div>
        <div className="second-part">
          <div className="pencil-icon">
            <PencilIcon color={theme === "light" ? "#3272BE" : "#329EE2"} />
          </div>
          <div className="text-part">
            <Typography
              label="Edit Plan"
              variant="h7Med"
              styles="custom-edit-text-style"
              onClick={() => navigate("/study-planner-form")}
            />
          </div>
        </div>
      </div>
      {isLoading ? (
        <Spinner theme={theme} />
      ) : isError ? (
        <div className={classNames("daughnut-chart-error-container")}>
          <FetchError content="Oops! failed to load resources." />
        </div>
      ) : (
        <>
          {data &&
          (data.NextSession === null ||
            data.NextSession === undefined) ? null : (
            <div
              className={classNames(
                "next-session-section",
                aiMCATChatDrawerStatus === "open" ? "flex-wrap" : ""
              )}
            >
              <div className="session-detail-section">
                <div className="session-title-section">
                  <Typography label="Next Session:" variant="h4Med" />
                </div>
                <div className="detail-section">
                  <div className="name-section">
                    <Typography
                      label={data?.NextSession?.ClassName}
                      variant="h7Med"
                    />
                  </div>
                  <div className="date-section">
                    <Typography
                      label={dayjs
                        .utc(data?.NextSession?.StartDateUtc)
                        .local()
                        .format("dddd, MMMM D")}
                      variant="h7Med"
                    />
                  </div>
                  <div className="time-section">
                    <Typography
                      label={`${dayjs
                        .utc(data?.NextSession?.StartDateUtc)
                        .local()
                        .format("h:mm a")} - ${dayjs
                        .utc(data.NextSession.EndDateUtc)
                        .local()
                        .format("h:mm a")} ${getTimeZone(
                        data?.NextSession?.EndDateUtc
                      )}`}
                      variant="h7Med"
                    />
                  </div>
                </div>
              </div>
              {data &&
              data.NextSession &&
              data.NextSession.LaunchUrl === null ? null : (
                <LaunchSessionButton data={data} />
              )}
            </div>
          )}
          <LoadingOverlay
            isLoading={
              validateModal.isShowModal === false &&
              (isValidateLoading || updateEventIsLoading)
            }
            theme={theme}
          >
            <div
              className={classNames(
                "calender-section",
                aiMCATChatDrawerStatus === "open" ? "table-cell-width-100" : ""
              )}
            >
              <div className="today-indicator-section">
                <div className="today-year-label-section">
                  <div className="today-label-section">
                    <Typography
                      variant="h8Med"
                      label="Today"
                      styles="today-typography-custom-style"
                      onClick={handleClickToday}
                    />
                  </div>
                  <MonthNavigator
                    incrementMonth={incrementMonth}
                    decrementMonth={decrementMonth}
                    text={currentMonth.format("MMMM YYYY")}
                    isRightDisable={dayjs(currentMonth).isSameOrAfter(
                      dayjs(maxMonthNo),
                      "month"
                    )}
                    isLeftDisable={dayjs(currentMonth).isSameOrBefore(
                      dayjs(minMonthNo),
                      "month"
                    )}
                    setIsMonthPopUpShow={setIsMonthPopUpShow}
                    setIsYearPopUpShow={setIsYearPopUpShow}
                    isMonthPopUpShow={isMonthPopUpShow}
                    isYearPopUpShow={isYearPopUpShow}
                    popUpIncrementMonth={popUpIncrementMonth}
                    popUpIncrementYears={popUpIncrementYears}
                    maxDate={maxDate}
                    minDate={minDate}
                    currentMonth={currentMonth}
                    theme={theme}
                  />
                </div>
                <div className="indicator-section">
                  <div className="first">
                    <BlueIndicator />
                    <Typography
                      variant="h9Book"
                      label="Homework"
                      styles="first-typography-custom-style"
                    />
                  </div>
                  <div className="second">
                    <RedIndicator />
                    <Typography
                      variant="h9Book"
                      label="Prework"
                      styles="second-typography-custom-style"
                    />
                  </div>
                  <div className="third">
                    <GreenIndicator />
                    <Typography
                      variant="h9Book"
                      label="Diagnostic Exam"
                      styles="third-typography-custom-style"
                    />
                  </div>
                </div>
              </div>

              {screen.width <= 575 ? (
                <div
                  className={classNames(
                    "study-planner-mobile-wrapper",
                    aiMCATChatDrawerStatus === "closed"
                      ? "study-planner-mobile-wrapper-ai-chat-drawer-closed"
                      : ""
                  )}
                >
                  <table className="study-table-mobile" rules="none">
                    <thead>
                      <tr>
                        {daysofTheWeek.map((weekday, index) => (
                          <th key={index}>
                            <Typography
                              variant="h8Med"
                              label={weekday[0]}
                              styles={
                                weekday[0] === "S"
                                  ? "day-name-custom-style-gray"
                                  : "day-name-custom-style-black"
                              }
                            />
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>{mobileRenderBody()}</tbody>
                  </table>
                  {mobileDay ? (
                    <div className="schedule-content-section">
                      {eventList &&
                        eventList
                          .filter(
                            (val: any) =>
                              dayjs(val.Start).format("DD/MM/YYYY") ===
                              dayjs(mobileDay).format("DD/MM/YYYY")
                          )
                          .sort((first: any, second: any) => {
                            if (first.EventOrder === 0) return 1;
                            if (second.EventOrder === 0) return -1;
                            else return first.EventOrder - second.EventOrder;
                          })
                          .map((value: any, index: number) => (
                            <div
                              key={index}
                              className="schedule-section-wrapper"
                            >
                              <StudyPlannerScheduleCard
                                cardData={value}
                                theme={theme}
                                day={mobileDay}
                              />
                            </div>
                          ))}
                      {eventList &&
                      eventList.filter(
                        (val: any) =>
                          dayjs(val.Start).format("DD/MM/YYYY") ===
                          dayjs(mobileDay).format("DD/MM/YYYY")
                      ).length > 0 ? (
                        <div className="time-container">
                          <Typography
                            label={sumOfTimeEstimates(
                              eventList,
                              dayjs(mobileDay).format("DD/MM/YYYY")
                            )}
                            variant="h9Book"
                            styles="custom-typography-style"
                          />
                        </div>
                      ) : null}

                      {mobileDay &&
                      dayjs(mobileDay).isSameOrAfter(
                        dayjs(data.StudyPlannerForm.StartDate)
                      ) &&
                      dayjs(mobileDay).isBefore(
                        dayjs(data.StudyPlannerForm.ExamDate)
                      ) ? (
                        <div className="add-new-item-wrapper">
                          <Button
                            icon={false}
                            label="Add To Study Plan"
                            type="score"
                            styles="mobile-add-event"
                            onClick={() => {
                              navigate("/add-to-study-plan", {
                                state: {
                                  date: formatToLocalDate(
                                    mobileDay,
                                    "YYYY-MM-DD"
                                  ),
                                },
                              });
                              mixpanelHelper.track("Add Study Plan Event", {
                                testType: formData.TestType,
                                productCode: productCode,
                                psoCode: offeringCode,
                                userID: userId,
                              });
                            }}
                          />
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="study-table-wrapper" ref={calendarSectionRef}>
                  <table className="study-table" rules="none">
                    <thead>
                      <tr>
                        {daysofTheWeek.map((weekday, index) => (
                          <th key={index}>
                            <Typography variant="h8Med" label={weekday} />
                          </th>
                        ))}
                      </tr>
                    </thead>
                    {data ? <tbody>{renderBody(data)}</tbody> : null}
                  </table>
                  <StudyModal
                    isOpen={modalState.showModal}
                    type={modalState.type}
                    currentState={modalState}
                    handleOnclose={handleModalClose}
                    validateRescheduleDate={validateRescheduleDate}
                    theme={theme}
                    isDelete={isDelete}
                    setIsDelete={setIsDelete}
                    setOpenRefreshModal={setRefreshModalOpenState}
                    handleSetCourseworkPage={handleSetCourseworkPage}
                    courseworkPage={courseworkPage}
                  />
                  <SideFilterPanel
                    isOpen={sideModalState.isOpen}
                    displayDate={sideModalState.displayDate}
                    handleOnclose={() =>
                      setSideModalState({ isOpen: false, displayDate: "" })
                    }
                    theme={theme}
                  />
                  <ValidateModal
                    isOpen={validateModal.isShowModal}
                    payload={validateModal.payload}
                    handleOnclose={handleValidateModalClose}
                    theme={theme}
                    cardInfo={validateModal.payload.eventInfo}
                    newDay={validateModal.payload.newDate}
                    performUpdate={handleUpdateEvent}
                    IsLoadingUpdateEvent={updateEventIsLoading}
                  />
                  <RefreshModal
                    theme={theme}
                    handleOnClose={handleRefreshModalClose}
                    isOpen={refreshModalOpenState.isOpen}
                    handleRefresh={handleRefresh}
                    isFetching={isFetching || isFetchingEvent}
                    contentObjectType={refreshModalOpenState.contentObjectType}
                  />

                  {aiMCATChatDrawerStatus === "open" && (
                    <HorizontalScrollHandler
                      containerRef={calendarSectionRef}
                      theme={theme}
                    />
                  )}
                </div>
              )}
            </div>
          </LoadingOverlay>
        </>
      )}
    </div>
  );
};
